import React, { useEffect, useState, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  Link2, RefreshCw, Copy, ExternalLink, Mail, XCircle, MoreHorizontal, CheckCircle2,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { useBranch } from "../../contexts/BranchContext";
import { can } from "../../utils/access";
import DataTable from "../Common/DataTable";
import CommonToaster from "../Common/CommonToaster";
import { PageHeader, AppButton, UserAvatar, EmptyState, ActionMenu, ActionMenuItem, ActionMenuDivider, ConfirmModal } from "../ui";
import "./fees-collection.scss";
import "./payment-links.scss";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" });
}

const STATUS_META = {
  active: { label: "Awaiting Payment", cls: "pl-badge--active" },
  paid: { label: "Paid", cls: "pl-badge--paid" },
  expired: { label: "Expired", cls: "pl-badge--muted" },
  cancelled: { label: "Cancelled", cls: "pl-badge--muted" },
};

const PaymentLinksPage = () => {
  const navigate = useNavigate();
  const { selectedBranchId } = useBranch();
  const canManage = can("fees.collect");

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/fee-payment-links", {
        params: statusFilter ? { status: statusFilter } : {},
      });
      setLinks(data.links || []);
    } catch {
      setLinks([]);
    }
    setLoading(false);
  }, [statusFilter]);

  // Refetch on status change (via fetchLinks) and when the selected branch changes.
  useEffect(() => { fetchLinks(); }, [fetchLinks, selectedBranchId]);

  const showToast = (message, type = "success") => setToast({ show: true, message, type });

  const handleCopy = async (link) => {
    try {
      await navigator.clipboard.writeText(link.url);
      showToast("Link copied to clipboard.");
    } catch {
      showToast("Could not copy link.", "error");
    }
  };

  const handleRefresh = async (link) => {
    setBusyId(link._id);
    try {
      const { data } = await apiClient.post(`/fee-payment-links/${link._id}/refresh`);
      setLinks((prev) => prev.map((l) => (l._id === link._id ? { ...l, ...data.link, url: l.url } : l)));
      showToast(data.message || "Status refreshed.", data.result?.changed ? "success" : "success");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to refresh status.", "error");
    }
    setBusyId(null);
  };

  const handleResend = async (link) => {
    setBusyId(link._id);
    try {
      const { data } = await apiClient.post(`/fee-payment-links/${link._id}/resend`);
      setLinks((prev) => prev.map((l) => (l._id === link._id ? { ...l, ...data.link, url: l.url } : l)));
      showToast(data.message || "Link re-sent.");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to resend link.", "error");
    }
    setBusyId(null);
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      await apiClient.post(`/fee-payment-links/${cancelTarget._id}/cancel`);
      setLinks((prev) => prev.map((l) => (l._id === cancelTarget._id ? { ...l, status: "cancelled" } : l)));
      setCancelTarget(null);
      showToast("Payment link cancelled.");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to cancel link.", "error");
    }
    setCancelLoading(false);
  };

  return (
    <div className="fees-page">
      <PageHeader
        title="Payment Links"
        description="Online fee payment links sent to students"
        icon={Link2}
        backButton
        onBack={() => navigate("/fees")}
      />

      <div className="fees-toolbar">
        <div className="fees-toolbar-left">
          <select
            className="fees-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="active">Awaiting Payment</option>
            <option value="paid">Paid</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <div className="fees-toolbar-right">
          <AppButton variant="secondary" size="sm" onClick={fetchLinks} disabled={loading}>
            <RefreshCw size={16} /> Refresh
          </AppButton>
        </div>
      </div>

      {loading ? (
        <div className="fees-loading">
          <Spinner animation="border" size="sm" />
          <span>Loading payment links...</span>
        </div>
      ) : links.length === 0 ? (
        <EmptyState
          icon={<Link2 size={40} />}
          title="No payment links yet"
          description="Generate one from Collect Fee → payment mode “Send Payment Link to Student”."
        />
      ) : (
        <DataTable
          data={links}
          columns={[
            {
              key: "student",
              label: "Student",
              render: (l) => (
                <div className="fees-cell-user" onClick={() => l.studentId?._id && navigate(`/students/${l.studentId._id}`)}>
                  <UserAvatar name={l.studentId?.name || "?"} size="sm" />
                  <div className="fees-cell-user__details">
                    <span className="fees-cell-user__name">{l.studentId?.name || "—"}</span>
                    {l.sentToEmail && <span className="fees-cell-user__email">{l.sentToEmail}</span>}
                  </div>
                </div>
              ),
            },
            {
              key: "course",
              label: "Course",
              render: (l) => l.courseId?.name || "—",
            },
            {
              key: "amount",
              label: "Amount",
              className: "fees-cell-paid",
              render: (l) => formatCurrency(l.amount),
            },
            {
              key: "status",
              label: "Status",
              render: (l) => {
                const m = STATUS_META[l.status] || STATUS_META.active;
                return <span className={`pl-badge ${m.cls}`}>{m.label}</span>;
              },
            },
            {
              key: "created",
              label: "Created",
              render: (l) => formatDate(l.createdAt),
            },
            {
              key: "expiry",
              label: "Paid / Expires",
              render: (l) => (l.status === "paid" ? `Paid ${formatDate(l.paidAt)}` : formatDate(l.expiresAt)),
            },
            {
              key: "actions",
              label: "Actions",
              className: "fees-cell-actions",
              render: (l) => (
                <ActionMenu trigger={busyId === l._id ? <Spinner animation="border" size="sm" /> : <MoreHorizontal size={16} />}>
                  <ActionMenuItem onClick={() => handleCopy(l)}>
                    <Copy size={14} /> Copy link
                  </ActionMenuItem>
                  <ActionMenuItem onClick={() => window.open(l.url, "_blank", "noopener")}>
                    <ExternalLink size={14} /> Open link
                  </ActionMenuItem>
                  {canManage && l.status !== "paid" && l.status !== "cancelled" && (
                    <ActionMenuItem onClick={() => handleRefresh(l)}>
                      <RefreshCw size={14} /> Refresh status
                    </ActionMenuItem>
                  )}
                  {l.status === "paid" && l.studentFeeId && l.studentId?._id && (
                    <ActionMenuItem onClick={() => navigate(`/students/${l.studentId._id}/fees/view/${l.studentFeeId}`)}>
                      <CheckCircle2 size={14} /> View receipt
                    </ActionMenuItem>
                  )}
                  {canManage && l.status !== "paid" && l.status !== "cancelled" && (
                    <ActionMenuItem onClick={() => handleResend(l)}>
                      <Mail size={14} /> Resend email
                    </ActionMenuItem>
                  )}
                  {canManage && l.status === "active" && (
                    <>
                      <ActionMenuDivider />
                      <ActionMenuItem danger onClick={() => setCancelTarget(l)}>
                        <XCircle size={14} /> Cancel link
                      </ActionMenuItem>
                    </>
                  )}
                </ActionMenu>
              ),
            },
          ]}
        />
      )}

      <ConfirmModal
        show={!!cancelTarget}
        onHide={() => { if (!cancelLoading) setCancelTarget(null); }}
        onConfirm={handleCancel}
        title="Cancel Payment Link"
        message={
          <p>
            Cancel the {formatCurrency(cancelTarget?.amount)} payment link for{" "}
            <strong>{cancelTarget?.studentId?.name || "this student"}</strong>? The link will stop working immediately.
          </p>
        }
        confirmText="Cancel Link"
        variant="danger"
        loading={cancelLoading}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default PaymentLinksPage;
